import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarClock, Loader2, Send, FileDown, Mail, ChevronDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

const EMPTY_SMTP = { smtp_host: "", smtp_port: 587, smtp_username: "", smtp_password: "", from_email: "", from_name: "" };

export function ReportDialog({ site, onSaved }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(site.report_recipient || "");
  const [day, setDay] = useState(site.report_day || 1);
  const [freq, setFreq] = useState(site.monitoring_frequency || "none");
  const [gate, setGate] = useState(site.gate_threshold ?? 80);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [smtp, setSmtp] = useState(EMPTY_SMTP);
  const [configured, setConfigured] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (open) {
      api.get(`/sites/${site.id}/email-config`).then((r) => {
        if (r.data && r.data.smtp_host) {
          setSmtp({ ...EMPTY_SMTP, ...r.data, smtp_password: "" });
          setConfigured(!!r.data.configured);
        }
      }).catch(() => {});
    }
  }, [open]);

  const setS = (k) => (e) => setSmtp({ ...smtp, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/sites/${site.id}`, { report_recipient: recipient, report_day: Number(day), monitoring_frequency: freq, gate_threshold: Number(gate) });
      toast.success("Report schedule saved");
      onSaved && onSaved({ report_recipient: recipient, report_day: Number(day), monitoring_frequency: freq, gate_threshold: Number(gate) });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await api.patch(`/sites/${site.id}`, { email_config: { ...smtp, smtp_port: Number(smtp.smtp_port) } });
      setConfigured(true);
      setSmtp({ ...smtp, smtp_password: "" });
      toast.success("Email settings saved (encrypted)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save email settings"); }
    finally { setSavingEmail(false); }
  };

  const testSend = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/sites/${site.id}/test-email`, { email_config: { ...smtp, smtp_port: Number(smtp.smtp_port) }, recipient });
      if (data.success) toast.success(`Test email sent to ${data.to}`);
      else toast.error(`Test failed: ${String(data.error || "unknown").slice(0, 80)}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Test failed"); }
    finally { setTesting(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/sites/${site.id}/send-report`);
      if (data.mocked) toast.message("Report queued (email not configured)", { description: `Would send to ${data.to}` });
      else if (data.success) toast.success(`Report emailed to ${data.to}`);
      else toast.error(`Send failed: ${String(data.error || "").slice(0, 80)}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/sites/${site.id}/report`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      link.download = "visibility-report.pdf";
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
    } catch { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="report-settings-button"><CalendarClock className="mr-2 h-4 w-4" /> Report</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report & schedule</DialogTitle>
          <DialogDescription>Auto-email a branded PDF to the client each month.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Recipient email</Label>
            <Input data-testid="report-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="client@company.com (defaults to admin)" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Frequency</Label>
              <select data-testid="report-frequency" value={freq} onChange={(e) => setFreq(e.target.value)} className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
                <option value="none">Off</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Send on day</Label>
              <select data-testid="report-day" value={day} onChange={(e) => setDay(e.target.value)} className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          {site.last_report_sent && <p className="text-xs text-muted-foreground">Last sent: {new Date(site.last_report_sent).toLocaleString()}</p>}
          <div className="space-y-2">
            <Label>Pass gate score (per client)</Label>
            <Input data-testid="gate-threshold" type="number" min={0} max={100} value={gate} onChange={(e) => setGate(e.target.value)} />
            <p className="text-xs text-muted-foreground">Sites scoring at/above this pass the visibility gate on the Enroll Timeline.</p>
          </div>
          <Button className="w-full" onClick={save} disabled={busy} data-testid="report-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save schedule
          </Button>

          <div className="rounded-md border border-border">
            <button onClick={() => setShowEmail(!showEmail)} className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium" data-testid="email-settings-toggle">
              <span className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email settings {configured && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-600 dark:text-emerald-400">configured</span>}</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showEmail ? "rotate-180" : ""}`} />
            </button>
            {showEmail && (
              <div className="space-y-3 border-t border-border p-3">
                <p className="text-xs text-muted-foreground">Per-site SMTP (e.g. Gmail: smtp.gmail.com : 587). Uses an app password. Stored encrypted.</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2 space-y-1"><Label className="text-xs">SMTP host</Label><Input data-testid="smtp-host" value={smtp.smtp_host} onChange={setS("smtp_host")} placeholder="smtp.gmail.com" /></div>
                  <div className="space-y-1"><Label className="text-xs">Port</Label><Input data-testid="smtp-port" value={smtp.smtp_port} onChange={setS("smtp_port")} placeholder="587" /></div>
                </div>
                <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="smtp-username" value={smtp.smtp_username} onChange={setS("smtp_username")} placeholder="you@gmail.com" /></div>
                <div className="space-y-1"><Label className="text-xs">Password / app password</Label><Input data-testid="smtp-password" type="password" value={smtp.smtp_password} onChange={setS("smtp_password")} placeholder={configured ? "••••• (leave blank to keep)" : ""} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1"><Label className="text-xs">From email</Label><Input data-testid="smtp-from-email" value={smtp.from_email} onChange={setS("from_email")} placeholder="reports@localpro.host" /></div>
                  <div className="space-y-1"><Label className="text-xs">From name</Label><Input data-testid="smtp-from-name" value={smtp.from_name} onChange={setS("from_name")} placeholder="LocalPro Beacon" /></div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={testSend} disabled={testing} data-testid="smtp-test-send">
                    {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Test send
                  </Button>
                  <Button className="flex-1" onClick={saveEmail} disabled={savingEmail} data-testid="smtp-save">
                    {savingEmail ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save email
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={sendNow} disabled={sending} data-testid="report-send-now">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send now
            </Button>
            <Button variant="outline" className="flex-1" onClick={download} disabled={downloading} data-testid="report-download">
              {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />} Download
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
